import { Fragment, useEffect } from "react";
import { Dialog, Transition } from "@headlessui/react";
import ChevronRightIcon from "@heroicons/react/20/solid/ChevronRightIcon";
import XMarkIcon from "@heroicons/react/20/solid/XMarkIcon";

import { useBreakpointContext } from "@hsl/core/hooks/useBreakpoint";
import { type FundDataWithType } from "@hsl/fund-explorer/metaSchemas";

import useFundPageURL from "../../../hooks/useFundPageURL";
import { Translate } from "../../../hooks/useTranslation";
import { ErrorBoundary } from "../../GenericErrorBoundary";
import { FundCardResponsiveContent } from "./FundCardResponsiveContent";
import FundCardTabs from "./FundCardTabs";
import FundSummary from "./FundSummary";

interface Props {
    fund: FundDataWithType;
    show: boolean;
    onClose: () => void;
    renderMessages: () => React.ReactNode;
    onSubscribeButtonClick?: (
        mode: "watch" | "subscribe",
    ) => (loggedIn: boolean) => void;
    isWatching?: boolean;
    isSubscribed?: boolean;
    itemInBasket?: boolean;
    hasUpdates?: boolean;
    initialTab?: string;
}

export const FundCardModal = ({
    show,
    onClose,
    fund,
    renderMessages,
    onSubscribeButtonClick,
    isWatching,
    isSubscribed,
    hasUpdates,
    initialTab,
}: Props) => {
    const { name, fundPageURL } = fund.data.data;
    const currentBreakpoint = useBreakpointContext()?.currentBreakpoint;
    const notMobile = currentBreakpoint !== "xs";
    const url = useFundPageURL(fundPageURL);

    useEffect(() => {
        if (notMobile) {
            onClose();
        }
    }, [notMobile, onClose]);

    return (
        <ErrorBoundary>
            <Transition show={show} as={Fragment}>
                <Dialog onClose={onClose} className="fixed inset-0 z-[10000]">
                    <Transition.Child
                        enter="transition-transform duration-300 ease-out"
                        enterFrom="transform scale-95 opacity-0"
                        enterTo="transform scale-100 opacity-100"
                        leave="transition-transform duration-200 ease-out"
                        leaveFrom="transform scale-100 opacity-100"
                        leaveTo="transform scale-95 opacity-0"
                    >
                        <Dialog.Panel className="h-dynamic-screen relative w-full overflow-auto bg-white">
                            <div className="border-platinum sticky top-0 z-50 flex w-full items-center gap-4 border-b bg-white p-4">
                                <a
                                    href={url ?? ""}
                                    target="_blank"
                                    className="flex-1"
                                    rel="noreferrer"
                                >
                                    <Dialog.Title className="text-core-blue text-xl leading-tight">
                                        {name}{" "}
                                    </Dialog.Title>
                                </a>
                                <button
                                    onClick={onClose}
                                    className="text-taupe-grey relative -top-px -mr-2 grid h-8 w-8 shrink-0 grow-0 place-items-center"
                                >
                                    <XMarkIcon />
                                </button>
                            </div>
                            <div className="px-4 pb-3 pt-1">
                                <FundCardResponsiveContent
                                    fund={fund}
                                    mode="card"
                                    showTitle={false}
                                    className="border-platinum border-b pb-3"
                                    onSubscribeButtonClick={
                                        onSubscribeButtonClick
                                    }
                                    isWatching={isWatching}
                                    isSubscribed={isSubscribed}
                                    hasUpdates={hasUpdates}
                                />
                                <FundSummary fund={fund} />
                                {renderMessages()}
                            </div>
                            <div className="border-platinum border-t-2 pb-20">
                                <ErrorBoundary>
                                    <FundCardTabs
                                        fund={fund}
                                        type="accordion"
                                        initialTab={initialTab}
                                    />
                                </ErrorBoundary>
                            </div>
                            <GoToFundPageButton fundPageURL={url ?? ""} />
                        </Dialog.Panel>
                    </Transition.Child>
                </Dialog>
            </Transition>
        </ErrorBoundary>
    );
};

const GoToFundPageButton = ({ fundPageURL }: { fundPageURL: string }) => (
    <div className="fixed bottom-0 left-0 z-50 w-full bg-gradient-to-t from-white via-white to-transparent px-2 pb-2">
        <a
            href={fundPageURL}
            className="bg-core-blue hover:bg-core-blue/90 focus-visible:outline-core-blue inline-flex  w-full items-center justify-center rounded-none px-2.5 py-2 text-base font-normal text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
            onClick={(e) => e.stopPropagation()}
        >
            <Translate>Go to fund page</Translate>
            <ChevronRightIcon className="-ml-0.5 h-6 w-6" aria-hidden="true" />
        </a>
    </div>
);
